import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { transactionApi, documentApi, clientApi } from '@/services/api'
import type { Journal, Document } from '@/types'
import { BookOpenCheck, Plus, Paperclip, FileText, Image, File } from 'lucide-react'
import { formatCurrency } from '@/utils/currency'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'

function DocIcon({ mimeType }: { mimeType: string }) {
  if (mimeType.startsWith('image/')) return <Image className="w-3 h-3 text-blue-400" />
  if (mimeType === 'application/pdf') return <FileText className="w-3 h-3 text-red-400" />
  return <File className="w-3 h-3 text-[#CCCED2]" />
}

function formatSize(bytes: number | null) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function JournalList() {
  const { clientId } = useParams<{ clientId: string }>()
  const [journals, setJournals] = useState<Journal[]>([])
  const [docs, setDocs] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [currency, setCurrency] = useState('USD')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    if (!clientId) return
    clientApi.get(clientId).then((r) => setCurrency(r.data.client.currency)).catch(() => {})
    Promise.all([
      transactionApi.listJournals(clientId),
      documentApi.list(clientId),
    ])
      .then(([journalRes, docRes]) => {
        setJournals(journalRes.data.journals)
        setDocs(docRes.data.documents)
      })
      .finally(() => setLoading(false))
  }, [clientId])

  function docsForJournal(journalId: string) {
    return docs.filter((d) => d.linked_journal_id === journalId)
  }

  function totalAmount(journal: Journal) {
    const total = journal.lines.reduce((sum, l) => sum + parseFloat(l.debit || '0'), 0)
    return total.toFixed(2)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Journal Entries</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Manual double-entry journal history</p>
        </div>
        <Link to={`/clients/${clientId}/journals/new`} className={cn(buttonVariants({ variant: 'create' }), 'gap-2')}>
          <Plus className="w-4 h-4" /> New Journal Entry
        </Link>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-5 space-y-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
          </CardContent>
        </Card>
      ) : journals.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <BookOpenCheck className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
            <p className="text-[15px] font-bold text-[#000A1E] mb-1">No journal entries yet</p>
            <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-4">
              Use journal entries for adjustments, accruals, and manual corrections.
            </p>
            <Link to={`/clients/${clientId}/journals/new`} className={cn(buttonVariants({ variant: 'create' }), 'gap-2 inline-flex')}>
              <Plus className="w-4 h-4" /> New Journal Entry
            </Link>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-[13px]">Date</TableHead>
                  <TableHead className="text-[13px]">Narration</TableHead>
                  <TableHead className="text-[13px]">Lines</TableHead>
                  <TableHead className="text-right text-[13px]">Amount</TableHead>
                  <TableHead className="text-[13px]">Type</TableHead>
                  <TableHead className="text-[13px]">Posted By</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {journals.map((journal) => {
                  const linkedDocs = docsForJournal(journal.id)
                  return (
                    <React.Fragment key={journal.id}>
                      <TableRow>
                        <TableCell className="font-mono text-[13px] text-[rgba(0,10,30,0.75)]">{journal.date}</TableCell>
                        <TableCell className="text-[15px] text-[#000A1E]">
                          {journal.narration || <span className="text-[#CCCED2]">&mdash;</span>}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5 max-w-xs">
                            {journal.lines.slice(0, 2).map((line, i) => (
                              <div key={i} className="text-[13px] text-[rgba(0,10,30,0.75)] truncate">
                                {line.account_code} {line.account_name}
                                <span className="ml-2 text-[#CCCED2]">
                                  {parseFloat(line.debit) > 0
                                    ? `Dr ${formatCurrency(line.debit, currency)}`
                                    : `Cr ${formatCurrency(line.credit, currency)}`}
                                </span>
                              </div>
                            ))}
                            {journal.lines.length > 2 && (
                              <div className="text-[13px] text-[#CCCED2]">+{journal.lines.length - 2} more lines</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[15px] text-[#000A1E]">
                          {formatCurrency(totalAmount(journal), currency)}
                        </TableCell>
                        <TableCell>
                          {journal.is_reversing ? (
                            <Badge variant="info">Reversing</Badge>
                          ) : (
                            <Badge variant="default">Standard</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-[15px] text-[rgba(0,10,30,0.75)]">
                          {journal.posted_by || <span className="text-[#CCCED2]">&mdash;</span>}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => setExpandedId(expandedId === journal.id ? null : journal.id)}
                            title="Attachments"
                          >
                            <Paperclip className={cn(
                              'w-3.5 h-3.5',
                              linkedDocs.length > 0 ? 'text-[#0078C8]' : 'text-[#CCCED2]',
                            )} />
                          </Button>
                        </TableCell>
                      </TableRow>
                      {expandedId === journal.id && (
                        <TableRow className="bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={7} className="px-6 py-3">
                            {linkedDocs.length === 0 ? (
                              <p className="text-[13px] text-[rgba(0,10,30,0.75)]">No documents attached to this journal.</p>
                            ) : (
                              <div className="flex flex-wrap gap-3">
                                {linkedDocs.map((doc) => (
                                  <a
                                    key={doc.id}
                                    href={doc.file_path}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-1.5 text-[13px] text-[#000A1E] hover:text-[#0078C8] bg-white border border-border rounded-md px-2.5 py-1.5 transition-colors"
                                  >
                                    <DocIcon mimeType={doc.mime_type} />
                                    <span className="font-bold truncate max-w-[160px]">{doc.file_name}</span>
                                    {doc.category && (
                                      <Badge variant="default" className="capitalize text-[10px] px-1 py-0">
                                        {doc.category}
                                      </Badge>
                                    )}
                                    <span className="text-[rgba(0,10,30,0.75)]">{formatSize(doc.file_size)}</span>
                                  </a>
                                ))}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
